import React, { useEffect } from "react";
import {
  Shield, Zap, Users, Target, Award, CheckCircle,
  ArrowRight, ChevronRight, AlertTriangle, Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};

const AboutUs = () => {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start('visible');
  }, [controls]);

  return (
    <div className="text-[#2c2c2c] overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden bg-white/40 border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="text-center"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div className="space-y-6" variants={fadeInUp}>
              <motion.div
                className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20"
                variants={fadeInUp}
              >
                <Users className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider">ABOUT US</span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900">
                Empowering the Future of <span className="text-[#005a9c]">AI Governance</span>
              </h1>

              <motion.p
                className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed"
                variants={fadeInUp}
              >
                At RAKFORT, we're building the next generation of AI governance solutions to help organizations harness the power of artificial intelligence responsibly and effectively.
              </motion.p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeInUp} className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20">
                <Target className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider">OUR MISSION</span>
              </div>

              <h2 className="text-4xl font-bold leading-tight text-slate-900">
                Building Trust in <span className="text-[#005a9c]">AI Systems</span> Worldwide
              </h2>

              <p className="text-lg text-gray-600 leading-relaxed">
                We believe that AI has the potential to transform industries and improve lives, but only if it's developed and deployed responsibly. Our mission is to provide the tools and frameworks that enable organizations to build trustworthy AI systems.
              </p>

              <div className="space-y-4">
                {[
                  { text: 'Ethical AI Development', icon: <CheckCircle className="w-5 h-5 text-green-600" /> },
                  { text: 'Regulatory Compliance', icon: <Shield className="w-5 h-5 text-blue-600" /> },
                  { text: 'Risk Management', icon: <AlertTriangle className="w-5 h-5 text-yellow-600" /> },
                  { text: 'Transparent AI Systems', icon: <Eye className="w-5 h-5 text-purple-600" /> }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    className="flex items-start space-x-3"
                    variants={fadeInUp}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {item.icon}
                    </div>
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
            >
              <div className="relative bg-white p-1 overflow-hidden shadow-xl border border-[#e5eef6] rounded-2xl">
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                  alt="AI Technology"
                  className="w-full h-auto rounded-xl opacity-90"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-transparent border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20 mb-6">
                <Users className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider">MEET THE TEAM</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                Our <span className="text-[#005a9c]">Global</span> Team
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                RakFort is driven by a globally diverse team of experts spanning the USA, Ireland, UK, Italy, and India. With deep experience in cybersecurity, AI research, penetration testing, and product engineering, our team brings a powerful blend of innovation and execution. United by a shared vision to secure generative AI, we combine strategic insight with hands-on technical excellence to build resilient, next-gen AI security solutions.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#005a9c] text-white overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-4xl font-bold mb-6">
                Ready to Transform Your AI Strategy?
              </h2>

              <p className="text-xl text-blue-100 mb-10 max-w-3xl mx-auto">
                Join leading enterprises that trust RAKFORT for their AI governance and security needs.
              </p>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <motion.button
                  onClick={() => navigate('/contactus')}
                  className="group relative bg-white text-[#005a9c] font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-slate-50 flex items-center"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>REQUEST A DEMO</span>
                    <ArrowRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
